import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DebugInfo {
  serviceWorkerStatus: string;
  pushManagerSupport: boolean;
  notificationPermission: NotificationPermission;
  hasActiveSubscription: boolean;
  subscriptionEndpoint?: string;
  vapidKeyAvailable?: boolean;
  isIOSPWA: boolean;
  iosVersion?: string;
}

export const PushNotificationDebug = () => {
  const { user } = useAuth();
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const info: DebugInfo = {
        serviceWorkerStatus: 'unknown',
        pushManagerSupport: 'PushManager' in window,
        notificationPermission: Notification.permission,
        hasActiveSubscription: false,
        isIOSPWA: isIOSPWA(),
        iosVersion: getIOSVersion()
      };

      // Check service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          if (registration.active) {
            info.serviceWorkerStatus = 'active';
          } else if (registration.installing) {
            info.serviceWorkerStatus = 'installing';
          } else if (registration.waiting) {
            info.serviceWorkerStatus = 'waiting';
          }

          // Check push subscription
          const subscription = await registration.pushManager?.getSubscription();
          if (subscription) {
            info.hasActiveSubscription = true;
            info.subscriptionEndpoint = subscription.endpoint.substring(0, 50) + '...';
          }
        } else {
          info.serviceWorkerStatus = 'not registered';
        }
      } else {
        info.serviceWorkerStatus = 'not supported';
      }

      // Check VAPID key availability
      try {
        const { data: vapidData } = await supabase.functions.invoke('get-vapid-keys');
        info.vapidKeyAvailable = !!vapidData?.publicKey;
      } catch (error) {
        info.vapidKeyAvailable = false;
      }

      setDebugInfo(info);
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const isIOSPWA = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = ('standalone' in navigator) && (navigator as any).standalone;
    return isIOS && isStandalone;
  };

  const getIOSVersion = () => {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      return `${match[1]}.${match[2]}`;
    }
    return undefined;
  };

  const StatusBadge = ({ status }: { status: boolean | string }) => {
    if (typeof status === 'boolean') {
      return status ? (
        <Badge variant="default" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          Yes
        </Badge>
      ) : (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          No
        </Badge>
      );
    }

    const isGood = status === 'active' || status === 'granted';
    return (
      <Badge variant={isGood ? 'default' : 'secondary'} className="gap-1">
        {isGood ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
        {status}
      </Badge>
    );
  };

  if (!debugInfo) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Push Notification Debug Info</CardTitle>
            <CardDescription>
              System status and diagnostics
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={checkStatus}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Service Worker</span>
            <StatusBadge status={debugInfo.serviceWorkerStatus} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Push Manager Support</span>
            <StatusBadge status={debugInfo.pushManagerSupport} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Notification Permission</span>
            <StatusBadge status={debugInfo.notificationPermission} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Active Subscription</span>
            <StatusBadge status={debugInfo.hasActiveSubscription} />
          </div>

          {debugInfo.subscriptionEndpoint && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Endpoint</span>
              <span className="text-xs text-muted-foreground font-mono">
                {debugInfo.subscriptionEndpoint}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">VAPID Key Available</span>
            <StatusBadge status={debugInfo.vapidKeyAvailable || false} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">iOS PWA Mode</span>
            <StatusBadge status={debugInfo.isIOSPWA} />
          </div>

          {debugInfo.iosVersion && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">iOS Version</span>
              <Badge variant="outline">{debugInfo.iosVersion}</Badge>
            </div>
          )}
        </div>

        {debugInfo.isIOSPWA && debugInfo.iosVersion && parseFloat(debugInfo.iosVersion) < 16.4 && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              ⚠️ iOS {debugInfo.iosVersion} detected. Push notifications require iOS 16.4 or later.
            </p>
          </div>
        )}

        {!debugInfo.isIOSPWA && /iPad|iPhone|iPod/.test(navigator.userAgent) && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 For best results on iPhone, add this app to your home screen.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
